import type {
	CSSProperties,
	IntrinsicElementAttributes,
	SVGAttributes,
} from 'vue';
import {
	DEFAULT_LINEHEIGHT, getBaselineOffsetPx,
} from './font';
import {
	applyTextTransform, createMeasurer, wrapLines,
} from './text';
import {
	SOFT_HYPHEN,
	type WidthScaleKey,
} from './text';

export interface DomElement<
	Tag extends keyof IntrinsicElementAttributes,
	Namespace = Tag extends keyof SVGElementTagNameMap ? 'http://www.w3.org/2000/svg' : never,
> {
	tag: Tag;
	props: Partial<IntrinsicElementAttributes[Tag]>;
	namespace?: Namespace;
	children?: Array<DomElement<keyof IntrinsicElementAttributes> | string>;
}

export interface FullyVisibleResult {
	isBottomVisible: boolean;
	isFullyVisible: boolean;
	isLeftVisible: boolean;
	isRightVisible: boolean;
	isTopVisible: boolean;
}

export interface InsertSoftHyphensOptions {
	fontFamily: string;
	fontSize: number;
	maxWidth: number;
	positionAttributes?: TextPositionAttributes;
	text: string;
}

export type TextPositionAttributes = Record<string, string> | null;

export type TextContentAlignment = 'center' | 'end' | 'start';

export interface TextContentOffsetOptions {
	alignContent: TextContentAlignment;
	lineCount: number;
	lineHeight: number;
	maxHeight?: number;
}

export interface TextLineLayout {
	contentOffsetY: number;
	contentWidth: number;
	lineHeightPx: number;
	lines: string[];
	paddingLeft: number;
	paddingRight: number;
}

export interface TextLineLayoutOptions {
	alignContent?: TextContentAlignment;
	fontFamily: string;
	fontSize: number;
	/**
	 * Explicit opt-in for regenerated SVG/textarea ink padding. Legacy non-SVG
	 * Canvas callers omit this so saved projects retain their historical layout.
	 */
	horizontalLayout?: TextHorizontalLayout;
	lineHeight?: number;
	maxHeight?: number;
	maxWidth: number;
	positionAttributes?: TextPositionAttributes;
	text: string;
	verticalLayout?: TextVerticalLayout;
}

export interface TextHorizontalLayout {
	contentWidth: number;
	paddingLeft: number;
	paddingRight: number;
}

export interface TextHorizontalLayoutOptions {
	fontFamily: string;
	fontSize: number;
	maxWidth: number;
	positionAttributes?: TextPositionAttributes;
	text: string;
}

export interface TextareaToDomElementTextOptions {
	alignContent?: TextContentAlignment;
	fill?: string;
	fontFamily: string;
	fontSize: number;
	horizontalLayout?: TextHorizontalLayout;
	lineHeight?: number;
	maxHeight?: number;
	maxWidth: number;
	positionAttributes?: TextPositionAttributes;
	text: string;
	textAlign?: CSSProperties['textAlign'];
	verticalLayout?: TextVerticalLayout;
}

export interface TextVerticalLayout {
	paddingBottom: number;
	paddingTop: number;
}

export interface TextVerticalLayoutOptions {
	positionAttributes?: Record<string, unknown> | null;
}

const softHyphensTextWidthCache = new Map<WidthScaleKey, Map<string, number>>();
let textHorizontalMetricsContext: CanvasRenderingContext2D | null = null;
let textTypographyMeasurer: HTMLDivElement | null = null;
const TEXT_WIDTH_POSITION_ATTRIBUTE_KEYS = [
	'font-kerning',
	'font-stretch',
	'font-style',
	'font-variant-caps',
	'font-weight',
	'letter-spacing',
	'text-rendering',
	'word-spacing',
] as const;
const TEXT_WIDTH_POSITION_ATTRIBUTE_DEFAULTS: Record<(typeof TEXT_WIDTH_POSITION_ATTRIBUTE_KEYS)[number], string> = {
	'font-kerning': 'auto',
	'font-stretch': 'normal',
	'font-style': 'normal',
	'font-variant-caps': 'normal',
	'font-weight': '400',
	'letter-spacing': 'normal',
	'text-rendering': 'auto',
	'word-spacing': 'normal',
};

/**
 * Configures the Canvas context used for line-edge glyph ink so it matches the
 * supported metric-affecting position typography.
 *
 * @param context Canvas context used for glyph-ink measurement.
 * @param fontFamily Font family used by the rendered text.
 * @param fontSize Font size in pixels.
 * @param positionAttributes Attributes inherited from the template position.
 * @param resolvedTypography Computed styles resolved by the hidden measurer.
 * @returns The configured Canvas context.
 */
function configureTextHorizontalMetricsContext(
	context: CanvasRenderingContext2D,
	fontFamily: string,
	fontSize: number,
	positionAttributes: TextPositionAttributes | undefined,
	resolvedTypography: CSSStyleDeclaration | undefined,
): CanvasRenderingContext2D {
	context.font = (
		resolvedTypography
			? [
				resolvedTypography.fontStyle || positionAttributes?.['font-style'] || 'normal',
				resolvedTypography.fontWeight || positionAttributes?.['font-weight'] || '400',
				`${fontSize}px`,
				fontFamily,
			].join(' ')
			: `${fontSize}px ${fontFamily}`
	);
	context.direction = 'ltr';
	context.fontKerning = (
		(resolvedTypography?.fontKerning || positionAttributes?.['font-kerning']) === 'none'
			? 'none'
			: (resolvedTypography?.fontKerning || positionAttributes?.['font-kerning']) === 'normal'
				? 'normal'
				: 'auto'
	);
	context.fontStretch = (
		resolvedTypography?.fontStretch
		|| positionAttributes?.['font-stretch']
		|| 'normal'
	) as CanvasFontStretch;
	context.fontVariantCaps = (
		resolvedTypography?.fontVariantCaps
		|| positionAttributes?.['font-variant-caps']
		|| 'normal'
	) as CanvasFontVariantCaps;
	const letterSpacing = (
		resolvedTypography?.letterSpacing
		|| positionAttributes?.['letter-spacing']
	);
	context.letterSpacing = (
		!letterSpacing
		|| letterSpacing === 'normal'
			? '0px'
			: letterSpacing
	);
	context.textAlign = 'left';
	context.textBaseline = 'alphabetic';
	context.textRendering = (
		resolvedTypography?.textRendering
		|| positionAttributes?.['text-rendering']
		|| 'auto'
	) as CanvasTextRendering;
	const wordSpacing = (
		resolvedTypography?.wordSpacing
		|| positionAttributes?.['word-spacing']
	);
	context.wordSpacing = (
		!wordSpacing
		|| wordSpacing === 'normal'
			? '0px'
			: wordSpacing
	);

	return context;
}

/**
 * Configures the hidden width measurer with the same metric-affecting
 * typography that is inherited by the SVG text and applied to its textarea.
 *
 * @param element Hidden element used for text-width measurement.
 * @param fontFamily Font family used by the rendered text.
 * @param fontSize Font size in pixels.
 * @param positionAttributes Attributes inherited from the template position.
 * @returns The configured hidden measurement element.
 */
function configureTextTypographyMeasurer(
	element: HTMLDivElement,
	fontFamily: string,
	fontSize: number,
	positionAttributes: TextPositionAttributes | undefined,
): HTMLDivElement {
	element.style.fontFamily = fontFamily;
	element.style.fontFeatureSettings = '"calt" 0, "rand" 0';
	element.style.fontSize = `${fontSize}px`;
	element.style.fontVariantLigatures = 'no-contextual';
	element.style.lineHeight = String(DEFAULT_LINEHEIGHT);
	element.style.textTransform = positionAttributes?.['text-transform'] ?? 'none';

	for (const attributeKey of TEXT_WIDTH_POSITION_ATTRIBUTE_KEYS) {
		element.style.setProperty(
			attributeKey,
			TEXT_WIDTH_POSITION_ATTRIBUTE_DEFAULTS[attributeKey],
		);
		const attributeValue = positionAttributes?.[attributeKey];

		if (attributeValue !== undefined) {
			element.style.setProperty(
				attributeKey,
				attributeValue,
			);
		}
	}

	return element;
}

/**
 * Creates a width measurer that mirrors position typography without changing
 * the historical measurement path for callers that do not use those styles.
 *
 * @param fontFamily Font family used by the rendered text.
 * @param fontSize Font size in pixels.
 * @param positionAttributes Attributes inherited from the template position.
 * @returns Function that measures rendered text advance width.
 */
function createTextTypographyMeasurer(
	fontFamily: string,
	fontSize: number,
	positionAttributes: TextPositionAttributes | undefined,
): (textToMeasure: string) => number {
	if (!hasTextWidthPositionAttributes(positionAttributes)) {
		return createMeasurer(
			fontFamily,
			fontSize,
		);
	}

	const measuredWidths = new Map<string, number>();
	const configuredElement = configureTextTypographyMeasurer(
		getTextTypographyMeasurer(),
		fontFamily,
		fontSize,
		positionAttributes,
	);
	const resolvedTextTransform = (
		getComputedStyle(configuredElement).textTransform
		|| configuredElement.style.textTransform
	);

	return (textToMeasure) => {
		const transformedText = applyTextTransform(
			textToMeasure,
			resolvedTextTransform,
		);
		const cachedWidth = measuredWidths.get(transformedText);

		if (cachedWidth !== undefined) {
			return cachedWidth;
		}

		const element = configureTextTypographyMeasurer(
			getTextTypographyMeasurer(),
			fontFamily,
			fontSize,
			positionAttributes,
		);
		element.style.textTransform = 'none';
		element.textContent = transformedText;
		const measuredWidth = element.getBoundingClientRect().width;
		measuredWidths.set(
			transformedText,
			measuredWidth,
		);

		return measuredWidth;
	};
}

/**
 * Returns the reusable Canvas context used to measure horizontal glyph ink.
 *
 * @returns A Canvas context when the runtime supports 2D measurement.
 */
function getTextHorizontalMetricsContext(): CanvasRenderingContext2D | undefined {
	if (textHorizontalMetricsContext) {
		return textHorizontalMetricsContext;
	}

	if (
		typeof document === 'undefined'
		|| typeof CanvasRenderingContext2D === 'undefined'
	) {
		return undefined;
	}

	const canvasElement = document.createElement('canvas');
	const context = canvasElement.getContext('2d');

	if (!context) {
		return undefined;
	}

	textHorizontalMetricsContext = context;

	return textHorizontalMetricsContext;
}

/**
 * Returns the shared hidden element used for typography-aware text widths.
 *
 * @returns Connected offscreen element used for synchronous width measurement.
 */
function getTextTypographyMeasurer(): HTMLDivElement {
	if (
		!textTypographyMeasurer
		|| !textTypographyMeasurer.isConnected
	) {
		textTypographyMeasurer = document.createElement('div');
		textTypographyMeasurer.style.border = '0 solid';
		textTypographyMeasurer.style.left = '-9999px';
		textTypographyMeasurer.style.margin = '0';
		textTypographyMeasurer.style.padding = '0';
		textTypographyMeasurer.style.position = 'absolute';
		textTypographyMeasurer.style.top = '-9999px';
		textTypographyMeasurer.style.visibility = 'hidden';
		textTypographyMeasurer.style.whiteSpace = 'nowrap';
		document.body.appendChild(textTypographyMeasurer);
	}

	return textTypographyMeasurer;
}

/**
 * Returns whether text-position attributes alter the horizontal glyph metrics.
 *
 * @param positionAttributes Attributes inherited from the template position.
 * @returns True when the typography-aware measurement path is required.
 */
function hasTextWidthPositionAttributes(positionAttributes: TextPositionAttributes | undefined): boolean {
	return (
		positionAttributes?.['text-transform'] !== undefined
		|| TEXT_WIDTH_POSITION_ATTRIBUTE_KEYS.some((attributeKey) => (
			positionAttributes?.[attributeKey] !== undefined
		))
	);
}

/**
 * Parses a page-space padding value that can be represented directly in SVG coordinates.
 *
 * @param value Candidate CSS pixel length.
 * @returns A finite non-negative pixel value, or zero for unsupported input.
 */
function parseTextPaddingPixels(value: unknown): number {
	if (typeof value !== 'string') {
		return 0;
	}

	const normalizedValue = value.trim();

	if (normalizedValue === '0') {
		return 0;
	}

	const match = /^\+?((?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)px$/i.exec(normalizedValue);

	if (!match) {
		return 0;
	}

	const parsedValue = Number(match[1]);

	return (
		Number.isFinite(parsedValue)
		&& parsedValue >= 0
			? parsedValue
			: 0
	);
}

/**
 * Produces a deep clone of a {@link DomElement} tree so it can be safely reused without side effects.
 *
 * @param element Root {@link DomElement} to clone.
 * @returns Deep-cloned {@link DomElement} instance.
 */
export function cloneDomElement(
	element: DomElement<keyof IntrinsicElementAttributes>,
): DomElement<keyof IntrinsicElementAttributes> {
	const clonedChildren = element.children?.map((child) => (
		typeof child === 'string'
			? child
			: cloneDomElement(child)
	));

	return {
		children: clonedChildren,
		namespace: element.namespace,
		props: (
			element.props
			?? {}
		),
		tag: element.tag,
	};
}

/**
 * Transforms the provided {@link SVGElement} tree into a {@link DomElement}
 * structure so it can be converted into a Vue {@link VNode}.
 *
 * @param element Root {@link SVGElement} of the parsed SVG document.
 * @returns {@link DomElement} describing the same SVG tree as {@link element}.
 */
export function convertSVGElementToDomElement<T extends SVGElement>(element: T): DomElement<keyof IntrinsicElementAttributes> {
	const tagName = element.tagName as keyof IntrinsicElementAttributes;
	const elementProps: SVGAttributes = {};

	type SvgAttributeEntry = {
		[AttributeName in keyof SVGAttributes]: {
			name: AttributeName;
			value: SVGAttributes[AttributeName];
		};
	}[keyof SVGAttributes];

	const attributes = Array.from(element.attributes) as SvgAttributeEntry[];

	for (const attribute of attributes) {
		if (!attribute) {
			continue;
		}

		elementProps[attribute.name] = attribute.value;
	}

	const children: Array<DomElement<keyof IntrinsicElementAttributes> | string> = [];
	const childNodes = Array.from(element.childNodes);

	for (const child of childNodes) {
		if (child.nodeType === Node.ELEMENT_NODE) {
			children.push(
				convertSVGElementToDomElement(child as SVGElement),
			);
		} else if (
			child.nodeType === Node.TEXT_NODE
			|| child.nodeType === Node.CDATA_SECTION_NODE
		) {
			const textContent = (
				child.textContent
				?? ''
			);

			if (textContent.length) {
				children.push(textContent);
			}
		}
	}

	return createDomElement(
		tagName,
		elementProps,
		children,
		'http://www.w3.org/2000/svg',
	);
}

export function createCanvasContext(font: string): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
	const canvasElement = (
		typeof OffscreenCanvas !== 'undefined'
			? new OffscreenCanvas(
				1,
				1,
			)
			: document.createElement('canvas')
	);
	const canvasContext = canvasElement.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;

	if (!canvasContext) {
		throw new Error('2D context not available');
	}

	canvasContext.font = font;

	return canvasContext;
}

export function createDomElement<
	Tag extends keyof IntrinsicElementAttributes,
	Namespace extends Tag extends keyof SVGElementTagNameMap ? 'http://www.w3.org/2000/svg' : never,
>(
	tag: Tag,
	props?: Partial<IntrinsicElementAttributes[Tag]>,
	children?: DomElement<Tag>['children'] | string,
	namespace?: Namespace,
): DomElement<Tag> {
	return {
		tag,
		namespace,
		props: (
			props
			?? {}
		),
		children: (
			typeof children === 'string'
				? [children]
				: children
		),
	};
}

export function domElementToHTMLElement(domElement: DomElement<keyof IntrinsicElementAttributes>): HTMLElement | SVGElement {
	let element: HTMLElement | SVGElement;

	if (!domElement.namespace) {
		element = document.createElement(domElement.tag);
	} else {
		element = document.createElementNS(
			domElement.namespace as 'http://www.w3.org/2000/svg',
			domElement.tag,
		) as SVGElement;
	}

	for (const [
		key,
		value,
	] of Object.entries(domElement.props)) {
		element.setAttribute(
			key,
			String(value),
		);
	}

	for (const child of domElement.children || []) {
		element.appendChild(
			typeof child === 'string'
				? document.createTextNode(child)
				: domElementToHTMLElement({
					...child,
					namespace: domElement.namespace,
				}),
		);
	}

	return element;
}

/**
 * Recursively searches for a DOM element that matches the provided predicate.
 *
 * @param element The DOM element to search within.
 * @param predicate A function that defines the search criteria.
 * @returns The found DOM element or null if not found.
 */
/**
 * Locates the first descendant {@link DomElement} whose tag matches the supplied `tag`.
 *
 * @param element Root {@link DomElement} to scan.
 * @param tag {@link keyof IntrinsicElementAttributes} value to match.
 * @returns Matching {@link DomElement} when discovered, otherwise `null`.
 */
export function findDomElement<Tag extends keyof IntrinsicElementAttributes>(
	element: DomElement<keyof IntrinsicElementAttributes>,
	tag: Tag,
): DomElement<Tag> | null;
/**
 * Locates the first descendant {@link DomElement} that satisfies the provided `predicate`.
 *
 * @param element Root {@link DomElement} to scan.
 * @param predicate Type guard narrowing a {@link DomElement} to the desired tag-specific element.
 * @returns Matching {@link DomElement} when discovered, otherwise `null`.
 */
export function findDomElement<Tag extends keyof IntrinsicElementAttributes>(
	element: DomElement<keyof IntrinsicElementAttributes>,
	predicate: (element: DomElement<keyof IntrinsicElementAttributes>) => element is DomElement<Tag>,
): DomElement<Tag> | null;
/**
 * Locates the first descendant {@link DomElement} that matches either a tag name or the supplied predicate.
 *
 * @param element Root {@link DomElement} to scan.
 * @param tagOrPredicate Tag name or predicate describing the desired {@link DomElement}.
 * @returns Matching {@link DomElement} when discovered, otherwise `null`.
 */
export function findDomElement<Tag extends keyof IntrinsicElementAttributes>(
	element: DomElement<keyof IntrinsicElementAttributes>,
	tagOrPredicate: Tag | ((element: DomElement<keyof IntrinsicElementAttributes>) => element is DomElement<Tag>),
): DomElement<Tag> | null {
	const predicate: (element: DomElement<keyof IntrinsicElementAttributes>) => element is DomElement<Tag> = (
		typeof tagOrPredicate === 'function'
			? tagOrPredicate
			: (candidate): candidate is DomElement<Tag> => candidate.tag === tagOrPredicate
	);

	if (predicate(element)) {
		return element;
	}

	if (element.children) {
		for (const child of element.children) {
			if (typeof child === 'string') {
				continue;
			}

			const found = findDomElement(
				child,
				predicate,
			);

			if (found) {
				return found;
			}
		}
	}

	return null;
}

/**
 * Compute the center of a DOMRect.
 * @param rect The DOMRect to compute the center of.
 * @returns A tuple containing the x and y coordinates of the center of the DOMRect.
 */
export function getDOMRectCenter(rect: DOMRect): [x: number, y: number] {
	return [
		rect.left + (rect.width / 2),
		rect.top + (rect.height / 2),
	];
}

/**
 * Calculates the free-space offset for a logical text line block inside a fixed-height box.
 *
 * @param options Text block geometry and the requested content alignment.
 * @param options.alignContent Supported vertical alignment contract.
 * @param options.lineCount Number of logical lines in the rendered block.
 * @param options.lineHeight Absolute height of each logical line in pixels.
 * @param options.maxHeight Available box height; an omitted value preserves start positioning.
 * @returns A non-negative vertical offset for the complete logical line block.
 */
export function getTextContentOffsetY(options: TextContentOffsetOptions): number {
	const {
		alignContent,
		lineCount,
		lineHeight,
		maxHeight,
	} = options;
	const availableVerticalSpace = (
		typeof maxHeight === 'number'
			? Math.max(
				0,
				maxHeight - lineCount * lineHeight,
			)
			: 0
	);

	if (alignContent === 'center') {
		return availableVerticalSpace / 2;
	}

	if (alignContent === 'end') {
		return availableVerticalSpace;
	}

	return 0;
}

/**
 * Calculates the horizontal safety insets required to keep painted glyph ink
 * inside a fixed-width text box. The routine repeatedly wraps and measures the
 * actual display lines because reserving an inset can expose a different glyph
 * at a line edge. Insets only grow between passes, preventing layout oscillation.
 *
 * @param options Text, font, and text-box width to measure.
 * @param options.fontFamily Font family used by the rendered text.
 * @param options.fontSize Font size in pixels.
 * @param options.maxWidth Outer width of the text box.
 * @param options.positionAttributes Metric-affecting template-position typography.
 * @param options.text Raw text whose possible line-edge glyphs are measured.
 * @returns Left and right safety padding plus the remaining content width.
 */
export function getTextHorizontalLayout(options: TextHorizontalLayoutOptions): TextHorizontalLayout {
	const {
		fontFamily,
		fontSize,
		maxWidth,
		positionAttributes,
		text,
	} = options;
	const context = getTextHorizontalMetricsContext();
	const configuredTypographyElement = (
		hasTextWidthPositionAttributes(positionAttributes)
			? configureTextTypographyMeasurer(
				getTextTypographyMeasurer(),
				fontFamily,
				fontSize,
				positionAttributes,
			)
			: undefined
	);
	const resolvedTypography = (
		configuredTypographyElement
			? getComputedStyle(configuredTypographyElement)
			: undefined
	);
	const resolvedTextTransform = (
		resolvedTypography?.textTransform
		|| positionAttributes?.['text-transform']
	);
	const measure = createTextTypographyMeasurer(
		fontFamily,
		fontSize,
		positionAttributes,
	);
	let paddingLeft = 0;
	let paddingRight = 0;

	if (context) {
		configureTextHorizontalMetricsContext(
			context,
			fontFamily,
			fontSize,
			positionAttributes,
			resolvedTypography,
		);
		let layoutStable = false;

		while (!layoutStable) {
			const contentWidth = Math.max(
				0,
				maxWidth - paddingLeft - paddingRight,
			);
			const normalizedText = insertSoftHyphens({
				fontFamily,
				fontSize,
				maxWidth: contentWidth,
				positionAttributes,
				text,
			});
			const displayLines = wrapLines(
				normalizedText,
				contentWidth,
				measure,
			).lines;
			let nextPaddingLeft = paddingLeft;
			let nextPaddingRight = paddingRight;

			for (const line of displayLines) {
				if (!line) {
					continue;
				}

				const metrics = context.measureText(applyTextTransform(
					line,
					resolvedTextTransform,
				));
				const linePaddingLeft = Math.max(
					0,
					metrics.actualBoundingBoxLeft,
				);
				const linePaddingRight = Math.max(
					0,
					metrics.actualBoundingBoxRight - metrics.width,
				);

				if (Number.isFinite(linePaddingLeft)) {
					nextPaddingLeft = Math.max(
						nextPaddingLeft,
						linePaddingLeft,
					);
				}

				if (Number.isFinite(linePaddingRight)) {
					nextPaddingRight = Math.max(
						nextPaddingRight,
						linePaddingRight,
					);
				}
			}

			layoutStable = (
				nextPaddingLeft === paddingLeft
				&& nextPaddingRight === paddingRight
			);

			paddingLeft = nextPaddingLeft;
			paddingRight = nextPaddingRight;
		}
	}

	return {
		contentWidth: Math.max(
			0,
			maxWidth - paddingLeft - paddingRight,
		),
		paddingLeft,
		paddingRight,
	};
}

/**
 * Wraps text and calculates the shared line-block geometry used by SVG and
 * Canvas text renderers.
 *
 * @param options Text, font, box, and vertical-alignment values to lay out.
 * @param options.alignContent Vertical alignment inside `maxHeight` (default: `start`).
 * @param options.fontFamily Font family used for line-width measurement.
 * @param options.fontSize Font size in pixels.
 * @param options.horizontalLayout Precomputed horizontal ink-safe layout; omitting it preserves the full legacy width.
 * @param options.lineHeight Line-height multiplier (default: {@link DEFAULT_LINEHEIGHT}).
 * @param options.maxHeight Available box height used to calculate the vertical offset.
 * @param options.maxWidth Maximum width of each wrapped line.
 * @param options.positionAttributes Metric-affecting template-position typography.
 * @param options.text Text that has already received any required soft-hyphen breakpoints.
 * @param options.verticalLayout Explicit vertical padding for SVG text; omitted callers preserve legacy layout.
 * @returns Wrapped display lines, their pixel line height, and the line-block offset.
 */
export function getTextLineLayout(options: TextLineLayoutOptions): TextLineLayout {
	const {
		alignContent = 'start',
		fontFamily,
		fontSize,
		horizontalLayout,
		lineHeight = DEFAULT_LINEHEIGHT,
		maxHeight,
		maxWidth,
		positionAttributes,
		text,
		verticalLayout,
	} = options;
	/**
	 * Keep horizontal ink safety opt-in: render-page can draw historical non-SVG
	 * text through callers of this helper, and those saved models must not reflow.
	 */
	const resolvedHorizontalLayout = (
		horizontalLayout
		?? {
			contentWidth: maxWidth,
			paddingLeft: 0,
			paddingRight: 0,
		}
	);
	const measure = createTextTypographyMeasurer(
		fontFamily,
		fontSize,
		positionAttributes,
	);
	const wrappedText = wrapLines(
		text,
		resolvedHorizontalLayout.contentWidth,
		measure,
	);
	const lineHeightPx = fontSize * lineHeight;
	const paddingBottom = verticalLayout?.paddingBottom ?? 0;
	const paddingTop = verticalLayout?.paddingTop ?? 0;
	const lines = wrappedText.lines.map((line) => line.replace(
		new RegExp(
			SOFT_HYPHEN,
			'g',
		),
		'-',
	));

	return {
		contentOffsetY: (
			paddingTop
			+ getTextContentOffsetY({
				alignContent,
				lineCount: lines.length,
				lineHeight: lineHeightPx,
				maxHeight: (
					typeof maxHeight === 'number'
						? Math.max(
							0,
							maxHeight - paddingTop - paddingBottom,
						)
						: undefined
				),
			})
		),
		...resolvedHorizontalLayout,
		lineHeightPx,
		lines,
	};
}

/**
 * Resolves supported vertical padding from template-position styles into page-space pixels.
 * Non-negative CSS `px` lengths are supported; other CSS units and expressions fall back
 * to zero because SVG geometry has no relative CSS sizing context.
 *
 * @param options Template-position attributes containing optional vertical padding.
 * @param options.positionAttributes Attributes inherited from the template position.
 * @returns A {@link TextVerticalLayout} with non-negative top and bottom padding.
 */
export function getTextVerticalLayout(options: TextVerticalLayoutOptions): TextVerticalLayout {
	const { positionAttributes } = options;

	return {
		paddingBottom: parseTextPaddingPixels(positionAttributes?.['padding-bottom']),
		paddingTop: parseTextPaddingPixels(positionAttributes?.['padding-top']),
	};
}

/**
 * Inject **soft-hyphen characters ({@link SOFT_HYPHEN})** at every position where the
 * browser would break a long word when the text is wrapped at `maxWidth`.
 *
 * The routine walks the input one code-point at a time while mirroring the
 * browser's line-wrapping rules:
 *
 * 1. **Natural breaks**
 *    When the next glyph still fits, copy it verbatim.
 * 2. **Whitespace opportunity**
 *    If the overflow happens *after* a space in the current line, we drop
 *    everything *after* that space to the next visual line (no hyphen).
 * 3. **In-word overflow**
 *    When the line already contains only a single word, we:
 *    * pull the **last glyph** off that line,
 *    * replace it with a soft-hyphen,
 *    * start the next line with the pulled glyph + the overflow glyph.
 *    This keeps the hyphen on the first line and avoids double-wide lines.
 *
 * New-line characters reset the width budget so explicit hard returns are
 * preserved unchanged.
 *
 * @param options.text        Original textarea value.
 * @param options.maxWidth    Line width limit in CSS pixels.
 * @param options.fontFamily  Font used in the textarea/SVG.
 * @param options.fontSize    Font size (px) used for precise measurement.
 * @param options.positionAttributes Metric-affecting template-position typography.
 *
 * @returns The same text but **augmented with {@link SOFT_HYPHEN}** so that subsequent
 *          layout engines (SVG/PDF) can break the word exactly like the
 *          browser does.
 */
export function insertSoftHyphens(options: InsertSoftHyphensOptions): string {
	const {
		text,
		maxWidth,
		fontFamily,
		fontSize,
		positionAttributes,
	} = options;
	const measure = (
		hasTextWidthPositionAttributes(positionAttributes)
			? createTextTypographyMeasurer(
				fontFamily,
				fontSize,
				positionAttributes,
			)
			: createMeasurer(
				fontFamily,
				fontSize,
				softHyphensTextWidthCache,
				/**
				 * Increased cache limit to 20000 since the soft hyphen insertion may
				 * involve measuring per word and per character, leading to a larger
				 * variety of strings being measured.
				 */
				20000,
			)
	);
	/**
	 * Initialize variables:
	 * - `result` to build the final text with soft-hyphens.
	 * - `currentLine` to track the current line being built.
	 */
	let result = '';
	let currentLine = '';

	/**
	 * 1. We split with a *capturing* regex so that `\n`/`\r\n` separators are
	 * returned as tokens as well (and preserved exactly).
	 */
	const paragraphs = text.split(/(\r?\n)/);

	for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex += 1) {
		const paragraph = paragraphs[paragraphIndex];

		/**
		 * 1.a Handle newline characters by resetting the current line.
		 */
		if (
			paragraph === '\n'
			|| paragraph === '\r\n'
		) {
			result += paragraph;
			currentLine = '';
			continue;
		}

		/**
		 * 1.b Skip empty paragraphs (can happen with consecutive newlines).
		 */
		if (!paragraph) {
			continue;
		}

		/**
		 * 1.c Check entire paragraph fit:
		 * 		- If yes, copy it verbatim
		 * 		- If no, fall through to phase 2
		 */
		if (measure(paragraph) <= maxWidth) {
			result += paragraph;
			currentLine = paragraph;
			continue;
		}

		/**
		 * 2. **Pre-scan by words** (tokens separated by whitespace)
		 *    This loop copies entire words as long as they fit.
		 *    It stops at the first overflowing word, which is then
		 *    processed by the original character-wise logic in phase 3.
		 */
		const tokens = paragraph.split(/(\s+)/);
		let consumedLength = 0;
		let overflowFound = false;

		for (let t = 0; t < tokens.length; t += 1) {
			const token = tokens[t];

			if (!token) {
				continue;
			}

			const tentative = currentLine + token;

			if (measure(tentative) <= maxWidth) {
				/**
				 * 2.a Token fits - copy it verbatim and continue to the next token.
				 */
				currentLine = tentative;
				result += token;
				consumedLength += token.length;
				continue;
			}

			/**
			 * 2.b First overflowing token encountered - stop the pre-scan here.
			 * The remainder of the paragraph (starting at this token) will
			 * be processed with the original character-wise logic.
			 */
			overflowFound = true;
			break;
		}

		/**
		 * 3. **Character-wise processing of the remainder** (after the pre-scan)
		 *    This loop processes the remainder of the paragraph one character
		 *    at a time, applying the line-breaking rules described above.
		 */
		if (overflowFound) {
			const remainder = paragraph.slice(consumedLength);

			for (const character of remainder) {
				/**
				 * Handle new-line characters by resetting the current line.
				 */
				if (
					character === '\n'
					|| character === '\r'
				) {
					result += character;
					currentLine = '';
					continue;
				}

				const tentative = currentLine + character;

				/**
				 * Check if the tentative line fits within the maxWidth.
				 * If it does, update the current line and result.
				 */
				if (measure(tentative) <= maxWidth) {
					currentLine = tentative;
					result += character;
					continue;
				}

				/**
				 * Find the last space in the current line to determine where to break.
				 */
				const lastSpaceIdx = currentLine.search(/\s(?=[^\s]*$)/);

				/**
				 * If a space is found, move the text after the last space to the next line.
				 */
				if (lastSpaceIdx !== -1) {
					const trailingWord = currentLine.slice(lastSpaceIdx + 1);
					currentLine = trailingWord + character;
					result += character;
					continue;
				}

				/**
				 * Handle in-word overflow:
				 * - Replace the last glyph with a soft-hyphen.
				 * - Start the next line with the last glyph and the current character.
				 */
				if (currentLine.length > 0) {
					const lastGlyph = currentLine.slice(-1);
					currentLine = currentLine.slice(
						0,
						-1,
					);
					result = result.slice(
						0,
						-1,
					) + SOFT_HYPHEN;
					const newSegment = lastGlyph + character;
					currentLine = newSegment;
					result += newSegment;
					continue;
				}

				/**
				 * If the current line is empty and the character doesn't fit,
				 * insert a soft-hyphen before the character.
				 */
				result += SOFT_HYPHEN + character;
				currentLine = character;
			}
		}
	}

	return result;
}

export function isFullyVisible(element: HTMLElement): FullyVisibleResult {
	const result: FullyVisibleResult = {
		isBottomVisible: true,
		isFullyVisible: true,
		isLeftVisible: true,
		isRightVisible: true,
		isTopVisible: true,
	};

	if (!element) {
		return {
			isBottomVisible: false,
			isFullyVisible: false,
			isLeftVisible: false,
			isRightVisible: false,
			isTopVisible: false,
		};
	}

	const elementRect = element.getBoundingClientRect();
	let currentElement = element;

	while (
		currentElement
		&& currentElement.parentElement
	) {
		const { parentElement } = currentElement;
		const parentRect = parentElement.getBoundingClientRect();

		const isScrollNotMatching = (
			element.scrollWidth > parentElement.scrollWidth
			|| element.scrollHeight > parentElement.scrollHeight
		);
		const isTopNotVisible = elementRect.top < parentRect.top;
		const isLeftNotVisible = elementRect.left < parentRect.left;
		const isRightNotVisible = Math.floor(parentRect.left + parentElement.scrollWidth) < Math.floor(elementRect.left + element.scrollWidth);
		const isBottomNotVisible = Math.floor(parentRect.top + parentElement.scrollHeight) < Math.floor(elementRect.top + element.scrollHeight);

		if (
			isScrollNotMatching
			|| isTopNotVisible
			|| isLeftNotVisible
			|| isRightNotVisible
			|| isBottomNotVisible
		) {
			result.isFullyVisible = false;
		}

		if (isTopNotVisible) {
			result.isTopVisible = false;
		}
		if (isLeftNotVisible) {
			result.isLeftVisible = false;
		}
		if (isRightNotVisible) {
			result.isRightVisible = false;
		}
		if (isBottomNotVisible) {
			result.isBottomVisible = false;
		}

		currentElement = currentElement.parentElement;
	}

	return result;
}

export function isOrHasParent(
	element: HTMLElement,
	parent: HTMLElement,
): boolean {
	if (element === parent) {
		return true;
	}

	if (!element.parentElement) {
		return false;
	}

	return isOrHasParent(
		element.parentElement,
		parent,
	);
}

export function isRectScrollable(
	element: HTMLElement,
	parentToStop?: HTMLElement,
): boolean {
	const isElementScrollable = !(
		element.scrollWidth <= element.clientWidth
		&& element.scrollHeight <= element.clientHeight
	);

	if (
		isElementScrollable
		|| !element.parentElement
		|| element.parentElement.id === 'webapp'
		|| parentToStop?.isSameNode(element.parentElement)
	) {
		return isElementScrollable;
	}

	return isRectScrollable(
		element.parentElement,
		parentToStop,
	);
}

export function onElementChildrenChange(
	element: Element,
	callback: () => void,
): () => void {
	if (!element) {
		return () => undefined;
	}

	const observersDisconnects: (() => void)[] = [];
	const onImageLoad = (
		target: HTMLElement,
		onLoad: () => void,
	) => {
		const styles = getComputedStyle(target);

		if (target instanceof HTMLImageElement) {
			if (
				target.complete
				&& target.naturalWidth !== 0
			) {
				onLoad();
			} else {
				target.addEventListener(
					'load',
					() => onLoad(),
				);
			}
		} else if (styles.backgroundImage !== 'none') {
			const image = new Image();
			image.addEventListener(
				'load',
				() => onLoad(),
			);

			if (styles.backgroundImage.startsWith('url(')) {
				image.src = styles.backgroundImage
					.slice(
						4,
						-1,
					)
					.replace(
						/("|')/g,
						'',
					);
			} else {
				image.src = styles.backgroundImage;
			}
		}
	};
	const checkForImagesLoad = (
		target: HTMLElement,
		onLoad: () => void,
	) => {
		onImageLoad(
			target,
			onLoad,
		);

		if (target.children.length > 0) {
			Array.prototype.forEach.call(
				target.children,
				(child: HTMLElement) => {
					checkForImagesLoad(
						child as HTMLElement,
						onLoad,
					);
				},
			);
		}
	};
	const mutationObserver = new MutationObserver((mutationsList) => {
		if (
			mutationsList.every((mutation) => (
				mutation.target instanceof HTMLInputElement
				|| mutation.attributeName === 'class'
				|| mutation.attributeName === 'style'
			))
		) {
			return;
		}

		callback();

		for (const mutation of mutationsList) {
			if (mutation.type === 'childList') {
				const target = mutation.target as HTMLElement;

				Array.prototype.forEach.call(
					target.children,
					(child: HTMLElement) => {
						onImageLoad(
							child,
							callback,
						);

						if (child.children.length > 0) {
							observersDisconnects.push(
								onElementChildrenChange(
									child,
									callback,
								),
							);
						}
					},
				);
			} else if (mutation.type === 'attributes') {
				if (mutation.target instanceof HTMLElement) {
					onImageLoad(
						mutation.target,
						callback,
					);
				}
			}
		}
	});
	mutationObserver.observe(
		element,
		{
			childList: true,
			subtree: true,
			attributes: true,
			characterData: true,
		},
	);
	const resizeObserver = new ResizeObserver(() => callback());
	resizeObserver.observe(element);
	observersDisconnects.push(() => resizeObserver.disconnect());
	observersDisconnects.push(() => mutationObserver.disconnect());
	checkForImagesLoad(
		element as HTMLElement,
		callback,
	);

	return () => {
		for (const observerDisconnect of observersDisconnects) {
			observerDisconnect();
		}
	};
}

/**
 * Parses the supported text content-alignment contract without coercing CSS
 * aliases or invalid values into a different layout.
 *
 * @param value Candidate value read from text position attributes.
 * @returns A supported {@link TextContentAlignment}, or `undefined` when the value is not part of the contract.
 */
export function parseTextContentAlignment(value: unknown): TextContentAlignment | undefined {
	if (
		value === 'center'
		|| value === 'end'
		|| value === 'start'
	) {
		return value;
	}

	return undefined;
}

export function stripHtmlEntities(text: string): string {
	return text
		.replace(
			/&nbsp;/g,
			' ',
		)
		.replace(
			/&amp;/g,
			'&',
		)
		.replace(
			/&lt;/g,
			'<',
		)
		.replace(
			/&gt;/g,
			'>',
		)
		.replace(
			/&quot;/g,
			'"',
		)
		.replace(
			/&apos;/g,
			"'",
		);
}

/**
 * Converts text from a textarea into an SVG `<text>` DOM element, applying
 * line wrapping, text alignment, and font styling. The text is wrapped to fit
 * within the specified `maxWidth`, and each line is positioned correctly
 * within the SVG using `<tspan>` elements.
 *
 * @param options - Configuration options for text conversion.
 * @param options.alignContent - Vertical alignment inside `maxHeight` (default: 'start').
 * @param options.fill - The color of the text (default: '#000').
 * @param options.fontFamily - The font family to use for the text.
 * @param options.fontSize - The font size in pixels.
 * @param options.horizontalLayout - Precomputed horizontal ink-safe layout; omitted callers keep the full legacy width.
 * @param options.lineHeight - The line height multiplier (default: {@link DEFAULT_LINEHEIGHT}).
 * @param options.maxHeight - The available height used to vertically align the wrapped line block.
 * @param options.maxWidth - The maximum width in pixels for each line.
 * @param options.positionAttributes - Metric-affecting template-position typography.
 * @param options.text - The text content to convert.
 * @param options.textAlign - The text alignment ('left', 'center', 'right'; default: 'left').
 * @param options.verticalLayout - Explicit SVG vertical padding; omitted callers preserve legacy layout.
 * @returns An SVG `<text>` DOM element containing the formatted text.
 */
export function textareaToDomElementText(options: TextareaToDomElementTextOptions): DomElement<'text'> {
	const {
		alignContent = 'start',
		fill = '#000',
		fontFamily,
		fontSize,
		horizontalLayout,
		lineHeight = DEFAULT_LINEHEIGHT,
		maxHeight,
		maxWidth,
		positionAttributes,
		text,
		textAlign = 'left',
		verticalLayout,
	} = options;
	const {
		contentOffsetY,
		contentWidth,
		lineHeightPx: dy,
		lines,
		paddingLeft,
		paddingRight,
	} = getTextLineLayout({
		alignContent,
		fontFamily,
		fontSize,
		horizontalLayout,
		lineHeight,
		maxHeight,
		maxWidth,
		positionAttributes,
		text,
		verticalLayout,
	});
	/**
	 * Preserve the existing first-line baseline for start alignment and move
	 * the complete logical line block for center or end alignment.
	 */
	const baseY = (
		getBaselineOffsetPx(
			fontFamily,
			fontSize,
			lineHeight,
		)
		+ contentOffsetY
	);
	/**
	 * Establish SVG anchoring instead of manual offsets.
	 * Using text-anchor avoids discrepancies between Canvas and SVG metrics
	 * for certain fonts (e.g., varying side-bearings), which caused
	 * misaligned/cropped right-aligned text previously.
	 */
	let textAnchor: 'start' | 'middle' | 'end' = 'start';
	let anchorX = paddingLeft;

	if (textAlign === 'center') {
		textAnchor = 'middle';
		anchorX = paddingLeft + contentWidth / 2;
	} else if (textAlign === 'right') {
		textAnchor = 'end';
		anchorX = maxWidth - paddingRight;
	}

	/**
	 * Create the base <text> element with font and position attributes.
	 * 'xml:space="preserve"' ensures that whitespace is preserved in the text.
	 */
	const textDomElement = createDomElement(
		'text',
		{
			x: anchorX,
			y: baseY,
			'font-family': fontFamily,
			'font-size': fontSize,
			fill,
			'text-anchor': textAnchor,
			'xml:space': 'preserve',
		} as Partial<SVGAttributes> & Record<'xml:space', string>,
		(
			lines.length
				? []
				: undefined
		),
		'http://www.w3.org/2000/svg',
	);

	/**
	 * Process each line to create <tspan> elements with proper positioning
	 */
	for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
		const line = lines[lineIndex];

		/**
		 * For empty lines, add a <tspan> with a space to ensure the line is rendered
		 */
		if (line === '') {
			textDomElement.children?.push(createDomElement(
				'tspan',
				{
					x: anchorX,
					dy: (
						lineIndex === 0
							? 0
							: dy
					),
				},
				' ',
			));
			continue;
		}

		/**
		 * Add a <tspan> for the line anchored via text-anchor and x.
		 */
		textDomElement.children?.push(createDomElement(
			'tspan',
			{
				x: anchorX,
				dy: (
					lineIndex === 0
						? 0
						: dy
				),
			},
			line,
		));
	}

	return textDomElement;
}
